import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import PageHeader from '../common/PageHeader';
import CharacterWritingCanvas, { CharacterWritingCanvasRef } from '../common/CharacterWritingCanvas';
import {
  verifyCharacter,
  savePracticeAttempt,
  getPracticeHistory,
  getCharacterStats,
  CharacterVerificationResult,
  PracticeAttempt,
  CharacterStatsResponse
} from '../../services/hindiCharacterService';
import './HindiCharacter.css';

interface HindiCharacterProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface AlphabetLetter {
  character: string;
  transliteration: string;
  pronunciation: string;
}

// Devanagari Alphabet Data (from HindiReading.tsx)
const DEVANAGARI_VOWELS: AlphabetLetter[] = [
  { character: 'अ', transliteration: 'a', pronunciation: 'like "u" in "but"' },
  { character: 'आ', transliteration: 'ā', pronunciation: 'like "a" in "father"' },
  { character: 'इ', transliteration: 'i', pronunciation: 'like "i" in "bit"' },
  { character: 'ई', transliteration: 'ī', pronunciation: 'like "ee" in "feet"' },
  { character: 'उ', transliteration: 'u', pronunciation: 'like "u" in "put"' },
  { character: 'ऊ', transliteration: 'ū', pronunciation: 'like "oo" in "boot"' },
  { character: 'ऋ', transliteration: 'ṛ', pronunciation: 'like "ri" in "risk"' },
  { character: 'ए', transliteration: 'e', pronunciation: 'like "ay" in "say"' },
  { character: 'ऐ', transliteration: 'ai', pronunciation: 'like "ai" in "aisle"' },
  { character: 'ओ', transliteration: 'o', pronunciation: 'like "o" in "go"' },
  { character: 'औ', transliteration: 'au', pronunciation: 'like "ow" in "cow"' },
  { character: 'अं', transliteration: 'aṃ', pronunciation: 'nasal sound' },
  { character: 'अः', transliteration: 'aḥ', pronunciation: 'aspirated sound' },
];

const DEVANAGARI_CONSONANTS: AlphabetLetter[] = [
  { character: 'क', transliteration: 'ka', pronunciation: 'like "k" in "kite"' },
  { character: 'ख', transliteration: 'kha', pronunciation: 'like "kh" in "blockhead"' },
  { character: 'ग', transliteration: 'ga', pronunciation: 'like "g" in "go"' },
  { character: 'घ', transliteration: 'gha', pronunciation: 'like "gh" in "doghouse"' },
  { character: 'ङ', transliteration: 'ṅa', pronunciation: 'like "ng" in "sing"' },
  { character: 'च', transliteration: 'ca', pronunciation: 'like "ch" in "church"' },
  { character: 'छ', transliteration: 'cha', pronunciation: 'aspirated "ch"' },
  { character: 'ज', transliteration: 'ja', pronunciation: 'like "j" in "jump"' },
  { character: 'झ', transliteration: 'jha', pronunciation: 'aspirated "j"' },
  { character: 'ञ', transliteration: 'ña', pronunciation: 'like "ny" in "canyon"' },
  { character: 'ट', transliteration: 'ṭa', pronunciation: 'retroflex "t"' },
  { character: 'ठ', transliteration: 'ṭha', pronunciation: 'aspirated retroflex "t"' },
  { character: 'ड', transliteration: 'ḍa', pronunciation: 'retroflex "d"' },
  { character: 'ढ', transliteration: 'ḍha', pronunciation: 'aspirated retroflex "d"' },
  { character: 'ण', transliteration: 'ṇa', pronunciation: 'retroflex "n"' },
  { character: 'त', transliteration: 'ta', pronunciation: 'like "t" in "table"' },
  { character: 'थ', transliteration: 'tha', pronunciation: 'aspirated "t"' },
  { character: 'द', transliteration: 'da', pronunciation: 'like "d" in "dog"' },
  { character: 'ध', transliteration: 'dha', pronunciation: 'aspirated "d"' },
  { character: 'न', transliteration: 'na', pronunciation: 'like "n" in "no"' },
  { character: 'प', transliteration: 'pa', pronunciation: 'like "p" in "pet"' },
  { character: 'फ', transliteration: 'pha', pronunciation: 'aspirated "p"' },
  { character: 'ब', transliteration: 'ba', pronunciation: 'like "b" in "ball"' },
  { character: 'भ', transliteration: 'bha', pronunciation: 'aspirated "b"' },
  { character: 'म', transliteration: 'ma', pronunciation: 'like "m" in "mother"' },
  { character: 'य', transliteration: 'ya', pronunciation: 'like "y" in "yes"' },
  { character: 'र', transliteration: 'ra', pronunciation: 'like "r" in "run"' },
  { character: 'ल', transliteration: 'la', pronunciation: 'like "l" in "love"' },
  { character: 'व', transliteration: 'va', pronunciation: 'like "v" in "voice"' },
  { character: 'श', transliteration: 'śa', pronunciation: 'like "sh" in "ship"' },
  { character: 'ष', transliteration: 'ṣa', pronunciation: 'retroflex "sh"' },
  { character: 'स', transliteration: 'sa', pronunciation: 'like "s" in "sun"' },
  { character: 'ह', transliteration: 'ha', pronunciation: 'like "h" in "hot"' },
  { character: 'क्ष', transliteration: 'kṣa', pronunciation: 'like "ksh"' },
  { character: 'त्र', transliteration: 'tra', pronunciation: 'like "tr"' },
  { character: 'ज्ञ', transliteration: 'jña', pronunciation: 'like "gya"' },
];

const ALL_CHARACTERS = [...DEVANAGARI_VOWELS, ...DEVANAGARI_CONSONANTS];

const HindiCharacter: React.FC<HindiCharacterProps> = ({
  user,
  onToggleSidebar
}) => {
  const canvasRef = useRef<CharacterWritingCanvasRef>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const [selectedCharacter, setSelectedCharacter] = useState<AlphabetLetter | null>(null);
  const [brushSize, setBrushSize] = useState(10);
  const [showTemplate, setShowTemplate] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<CharacterVerificationResult | null>(null);
  const [practiceHistory, setPracticeHistory] = useState<PracticeAttempt[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [characterFilter, setCharacterFilter] = useState<string>('');
  const [showVowels, setShowVowels] = useState(true);
  const [showConsonants, setShowConsonants] = useState(true);


  // Load practice history on mount
  useEffect(() => {
    loadPracticeHistory();
  }, [characterFilter]);

  const loadPracticeHistory = async () => {
    try {
      const result = await getPracticeHistory(20, 0, characterFilter || undefined);
      setPracticeHistory(result.attempts);
    } catch (error) {
      console.error('Failed to load practice history:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const clearCanvas = () => {
    canvasRef.current?.clear();
    setVerificationResult(null);
  };

  const handleCharacterSelect = (letter: AlphabetLetter) => {
    setSelectedCharacter(letter);
    clearCanvas();
    setVerificationResult(null);
  };

  const handleSubmit = async () => {
    if (!selectedCharacter || !canvasRef.current) return;

    if (!canvasRef.current.hasDrawing()) {
      alert('Please draw a character before submitting.');
      return;
    }

    const imageData = canvasRef.current.getImageData();
    if (!imageData) {
      alert('Failed to get image data. Please try again.');
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      // Verify character
      const result = await verifyCharacter(imageData, selectedCharacter.character);
      setVerificationResult(result);

      // Save practice attempt
      await savePracticeAttempt({
        character: selectedCharacter.character,
        image_data: imageData,
        is_correct: result.is_correct,
        score: result.confidence_score,
        feedback: result.feedback
      });

      // Reload history
      await loadPracticeHistory();
    } catch (error) {
      console.error('Failed to verify character:', error);
      alert('Failed to verify character. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleShowHistory = () => {
    setShowMenu(false);
    setShowHistory(!showHistory);
  };

  const handleClearFilter = () => {
    setCharacterFilter('');
  };

  return (
    <div className="hindi-character-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">
            Hindi Character Writing
          </h1>
        }
        rightActions={
          <div className="hindi-character-menu-container" ref={menuRef}>
            <button
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="hindi-character-menu-dropdown">
                <button
                  className="hindi-character-menu-item"
                  onClick={handleShowHistory}
                >
                  {showHistory ? '📚 Hide History' : '📚 Show History'}
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="hindi-character-content">
        {/* Character Selector */}
        <div className="hindi-character-section">
          <div className="hindi-character-section-header">/characters</div>
          <div className="hindi-character-selector">
            <div className="hindi-character-category">
              <button 
                className="hindi-character-category-toggle"
                onClick={() => setShowVowels(!showVowels)}
              >
                <span className="hindi-character-category-title">Vowels (स्वर)</span>
                <span className="hindi-character-toggle-arrow">{showVowels ? '▼' : '▶'}</span>
              </button>
              {showVowels && (
                <div className="hindi-character-grid">
                  {DEVANAGARI_VOWELS.map((letter, index) => (
                    <button
                      key={`vowel-${index}`}
                      className={`hindi-character-card ${selectedCharacter?.character === letter.character ? 'selected' : ''}`}
                      onClick={() => handleCharacterSelect(letter)}
                    >
                      <div className="hindi-character-char">{letter.character}</div>
                      <div className="hindi-character-trans">{letter.transliteration}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="hindi-character-category">
              <button 
                className="hindi-character-category-toggle"
                onClick={() => setShowConsonants(!showConsonants)}
              >
                <span className="hindi-character-category-title">Consonants (व्यंजन)</span>
                <span className="hindi-character-toggle-arrow">{showConsonants ? '▼' : '▶'}</span>
              </button>
              {showConsonants && (
                <div className="hindi-character-grid">
                  {DEVANAGARI_CONSONANTS.map((letter, index) => (
                    <button
                      key={`consonant-${index}`}
                      className={`hindi-character-card ${selectedCharacter?.character === letter.character ? 'selected' : ''}`}
                      onClick={() => handleCharacterSelect(letter)}
                    >
                      <div className="hindi-character-char">{letter.character}</div>
                      <div className="hindi-character-trans">{letter.transliteration}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drawing Area */}
        {selectedCharacter && (
          <div className="hindi-character-drawing-section">
            <div className="hindi-character-section-header">/practice</div>
            <div className="hindi-character-drawing-area">
              <CharacterWritingCanvas
                ref={canvasRef}
                character={selectedCharacter.character}
                showTemplate={showTemplate}
                brushSize={brushSize}
                templateFont='200px "Noto Sans Devanagari", "Mangal", sans-serif'
              />
              <div className="hindi-character-controls">
                <div className="hindi-character-control-group">
                  <label className="hindi-character-control-label">Brush Size</label>
                  <input
                    type="range"
                    min="6"
                    max="16"
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                    className="hindi-character-slider"
                  />
                  <span className="hindi-character-slider-value">{brushSize}px</span>
                </div>
                <button
                  className="hindi-character-control-btn"
                  onClick={() => setShowTemplate(!showTemplate)}
                >
                  {showTemplate ? '👁️ Hide Template' : '👁️ Show Template'}
                </button>
                <button
                  className="hindi-character-control-btn"
                  onClick={clearCanvas}
                >
                  Clear
                </button>
                <button
                  className="hindi-character-control-btn hindi-character-submit-btn"
                  onClick={handleSubmit}
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Submit'}
                </button>
              </div>
            </div>

            {/* Verification Result */}
            {verificationResult && (
              <div className={`hindi-character-result ${verificationResult.is_correct ? 'correct' : 'incorrect'}`}>
                <div className="hindi-character-result-header">
                  <span className="hindi-character-result-status">
                    {verificationResult.is_correct ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                  <span className="hindi-character-result-score">
                    Score: {(verificationResult.confidence_score ?? 0).toFixed(0)}%
                  </span>
                </div>
                <div className="hindi-character-result-feedback">
                  {verificationResult.feedback}
                </div>
                {verificationResult.suggestions && verificationResult.suggestions.length > 0 && (
                  <div className="hindi-character-result-suggestions">
                    <strong>Suggestions:</strong>
                    <ul>
                      {verificationResult.suggestions.map((suggestion, index) => (
                        <li key={index}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Practice History */}
        {showHistory && (
          <div className="hindi-character-history-section">
            <div className="hindi-character-section-header">/history</div>
            <div className="hindi-character-history-controls">
              <input
                type="text"
                placeholder="Filter by character..."
                value={characterFilter}
                onChange={(e) => setCharacterFilter(e.target.value)}
                className="hindi-character-filter-input"
              />
              {characterFilter && (
                <button
                  className="hindi-character-control-btn"
                  onClick={handleClearFilter}
                >
                  Clear Filter
                </button>
              )}
            </div>
            <div className="hindi-character-history-list">
              {practiceHistory.length === 0 ? (
                <div className="hindi-character-history-empty">
                  No practice attempts yet. Start practicing to see your history here.
                </div>
              ) : (
                practiceHistory.map((attempt) => (
                  <div
                    key={attempt.id}
                    className={`hindi-character-history-item ${attempt.is_correct ? 'correct' : 'incorrect'}`}
                  >
                    <div className="hindi-character-history-char">{attempt.character}</div>
                    <div className="hindi-character-history-details">
                      <span className="hindi-character-history-score">
                        {(attempt.score ?? 0).toFixed(0)}%
                      </span>
                      <span className="hindi-character-history-status">
                        {attempt.is_correct ? '✓' : '✗'}
                      </span>
                      <span className="hindi-character-history-date">
                        {attempt.created_at
                          ? new Date(attempt.created_at).toLocaleDateString()
                          : ''}
                      </span>
                    </div>
                    <div className="hindi-character-history-feedback">{attempt.feedback}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HindiCharacter;

